/* eslint-disable import/no-unresolved */
import { useState, useEffect } from 'react';
import { useMutation, useLazyQuery, useReactiveVar } from '@apollo/client';
import { saveAs } from 'file-saver';

import {
    showConfigureDataSourceModalVar, tenantIdVar,
    dataSourceReportIdVar,
} from 'Apollo/config';
import { GET_REPORT_SOURCE, DOWNLOAD_REPORT_TEMPLATE } from 'Apollo/Queries/CrossReportValidation';
import { ASSIGN_DATA_SOURCE } from 'Apollo/Mutations/CrossReportValidation';
import DataSourceModal from '../components/DataSourceModal';

const DataSourceModalContainer = ({ showDataSourceModal, reportList }) => {
    const [dataSourceReportId, setDataSourceReportId] = useState(dataSourceReportIdVar);
    const [dataSourceReportPeriod, setDataSourceReportPeriod] = useState(null);
    const [dataSourceReport, setDataSourceReport] = useState(null);
    const [isDataAvailableInRuleEngine, setIsDataAvailableInRuleEngine] = useState(null);
    const [isFileCurrentSource, setIsFileCurrentSource] = useState(true);
    const [batchList, setBatchList] = useState(null);
    const [dataSourceBatchId, setDataSourceBatchId] = useState(null);
    const [dataSourceFileId, setDataSourceFileId] = useState(null);
    const [showAddFilesModal, setShowAddFilesModal] = useState(false);
    const [currentSourceFileName, setCurrentSourceFileName] = useState(null);
    const [dataSourceFileName, setDataSourceFileName] = useState(null);
    const [disableSaveButton, setDisableSaveButton] = useState(true);
    const tenantId = useReactiveVar(tenantIdVar);

    const [getReportSource, { data, loading, error }] = useLazyQuery(GET_REPORT_SOURCE, {
        fetchPolicy: 'no-cache',
    });

    const [getReportTemplate, { data: reportTemplateData, loading: reportTemplateLoading, error: reportTemplateError }] = useLazyQuery(DOWNLOAD_REPORT_TEMPLATE, {
        fetchPolicy: 'no-cache',
    });

    const [assignDataSource, { data: assignDataSourceData, loading: assignDataSourceLoading, error: assignDataSourceError }] = useMutation(ASSIGN_DATA_SOURCE, {
        variables: {
            reportId: dataSourceReportId,
            reportPeriod: dataSourceReportPeriod,
            batchId: dataSourceBatchId,
            fileId: dataSourceFileId,

        },
        fetchPolicy: 'no-cache',
    });

    const onSaveDataSource = async () => {
        await assignDataSource();
        setDisableSaveButton(true);
    };

    const onDataSourceCancel = () => {
        showConfigureDataSourceModalVar(false);
    };

    const onFileExplorerClicked = () => {
        setShowAddFilesModal(true);
    };

    const onFileExplorerClosed = () => {
        setShowAddFilesModal(false);
        // showConfigureDataSourceModalVar(false);
        getReportSource({
            variables: {
                tenantId,
                reportId: dataSourceReportId,
                reportPeriod: dataSourceReportPeriod,
            },
        });
    };

    const onDownloadTemplate = () => {
        getReportTemplate({
            variables: {
                reportId: dataSourceReport.id,
            },
        });
    };

    useEffect(() => {
        if (dataSourceReportId) {
            setDataSourceBatchId(null);
            setDataSourceFileId(null);
            setDataSourceReportPeriod(null);
            setIsDataAvailableInRuleEngine(null);
            setIsFileCurrentSource(null);
            const selectedReport = reportList.filter(report => report.id === dataSourceReportId)[0];
            setDataSourceReport(selectedReport);
            if (!selectedReport.hasPeriod) {
                getReportSource({
                    variables: {
                        tenantId,
                        reportId: dataSourceReportId,
                        reportPeriod: dataSourceReportPeriod,
                    },
                });
            }
        }
    }, [dataSourceReportId]);

    useEffect(() => {
        if (dataSourceReportPeriod) {
            getReportSource({
                variables: {
                    tenantId,
                    reportId: dataSourceReportId,
                    reportPeriod: dataSourceReportPeriod,
                },
            });
        }
    }, [dataSourceReportPeriod]);

    useEffect(() => {
        if (data?.DQgetReportSource && data?.DQgetReportSource.ruleEnabled) {
            setIsDataAvailableInRuleEngine(data?.DQgetReportSource.ruleEnabled);
            setDataSourceBatchId(data?.DQgetReportSource.currentBatchId);
        } else {
            setIsDataAvailableInRuleEngine(data?.DQgetReportSource.ruleEnabled);
        }
        setBatchList(data?.DQgetReportSource.batchList);
        setCurrentSourceFileName(data?.DQgetReportSource.currentFileName);
        if (data?.DQgetReportSource.currentSource === 'batch') {
            setIsFileCurrentSource(false);
        } else {
            setIsFileCurrentSource(true);
        }
    }, [data]);

    useEffect(() => {
        if (dataSourceFileName) {
            setCurrentSourceFileName(dataSourceFileName);
            setShowAddFilesModal(false);
            setDisableSaveButton(false);
        }
    }, [dataSourceFileName]);

    useEffect(() => {
        if (assignDataSourceData && assignDataSourceData?.DQassignDataSource) {
            onFileExplorerClosed();
        }
    }, [assignDataSourceData]);

    useEffect(() => {
        if (!reportTemplateLoading && reportTemplateData && reportTemplateData.DQdownloadTemplate) {
            const name = reportTemplateData.DQdownloadTemplate.fileName;
            const url = reportTemplateData.DQdownloadTemplate.fileUrl;
            saveAs(url, `${name}`);
        }
    }, [reportTemplateLoading, reportTemplateData]);

    return (
        <DataSourceModal
            showDataSourceModal={showDataSourceModal}
            dataSourceReportId={dataSourceReportId}
            setDataSourceReportId={setDataSourceReportId}
            dataSourceReport={dataSourceReport}
            dataSourceReportPeriod={dataSourceReportPeriod}
            setDataSourceReportPeriod={setDataSourceReportPeriod}
            reportList={reportList}
            onDataSourceCancel={onDataSourceCancel}
            onSaveDataSource={onSaveDataSource}
            isDataAvailableInRuleEngine={isDataAvailableInRuleEngine}
            setIsDataAvailableInRuleEngine={setIsDataAvailableInRuleEngine}
            onFileExplorerClicked={onFileExplorerClicked}
            batchList={batchList}
            dataSourceBatchId={dataSourceBatchId}
            setDataSourceBatchId={setDataSourceBatchId}
            showAddFilesModal={showAddFilesModal}
            setShowAddFilesModal={setShowAddFilesModal}
            tenantId={tenantId}
            assignDataSourceLoading={assignDataSourceLoading}
            onFileExplorerClosed={onFileExplorerClosed}
            setDataSourceFileId={setDataSourceFileId}
            reportSourceLoading={loading}
            currentSourceFileName={currentSourceFileName}
            onDownloadTemplate={onDownloadTemplate}
            downloadTemplateLoading={reportTemplateLoading}
            isFileCurrentSource={isFileCurrentSource}
            setIsFileCurrentSource={setIsFileCurrentSource}
            setDataSourceFileName={setDataSourceFileName}
            disableSaveButton={disableSaveButton}
            setDisableSaveButton={setDisableSaveButton}
        />
    );
};

export default DataSourceModalContainer;
